import { useIntl } from 'react-intl';

import { commonMessages } from 'messages';
import { EAuthRoutes, FSEAuthRoutes } from '@agco-onedigital/ecosystem-lib-frontend-utils';
import { ISidebarProps, ISidebarItem, IListItem } from './types';
import { useManualsTitleDescUrl } from '../utils';

const messages = commonMessages.menu;
const newFeatureMessages = commonMessages.general;

export const useTopItems = ({
  isEcomEnabled,
  ecomUrl,
  isCpqEnabled,
  isManualsEnabled,
  isRepairManualsEnabled,
  isWarrantyEnabled,
  isPSPEnabled,
  isDTCEnabled,
  isVideosEnabled,
  isServiceAndInformationBasePathEnabled,
  newFeatureItems,
  accountItems,
  activeAccountId,
  onAccountSelect,
}: ISidebarProps) => {
  const { formatMessage } = useIntl();
  const { manualsTitle } = useManualsTitleDescUrl();
  const activeAccountName = accountItems.find(account => account.id === activeAccountId)?.label ?? '';

  const ECOMSidebarItem: ISidebarItem = {
    label: formatMessage(messages.partShop),
    icon: 'shopping_cart_checkout',
    to: ecomUrl,
    testId: 'sidebar-part-shop',
    isTargetBlank: true,
  };

  const CPQSidebarItem: ISidebarItem = {
    label: formatMessage(messages.newMachine),
    icon: 'add_home',
    to: 'https://cpq.agcocorp.com/',
    testId: 'sidebar-new-machine',
    isTargetBlank: true,
  };

  const warranty: IListItem = {
    label: formatMessage(messages.warranty),
    key: 'fleet-warranty',
    to: `${EAuthRoutes.FLEET_OVERVIEW}/warranty`,
    testId: 'sidebar-warranty',
  };

  const manuals: IListItem = {
    label: manualsTitle,
    key: 'manuals',
    to: isRepairManualsEnabled
      ? `${FSEAuthRoutes.SERVICE_AND_INFORMATION}/search-manuals`
      : `${FSEAuthRoutes.SERVICE_AND_INFORMATION}/find-manuals`,
    testId: 'sidebar-manuals',
  };

  const newManuals: IListItem = {
    label: 'New Manuals - only for testing',
    key: 'new-manuals',
    to: FSEAuthRoutes.NEW_MANUALS,
    testId: 'sidebar-new-manuals',
  };

  const psp: IListItem = {
    label: `${formatMessage(messages.psp)}`,
    key: 'fleet-psp',
    to: `${EAuthRoutes.FLEET_OVERVIEW}/psps`,
    testId: 'sidebar-psp',
  };

  const dtc: IListItem = {
    label: formatMessage(messages.diagnosticTroubleCodes),
    to: FSEAuthRoutes.DIAGNOSTIC_TROUBLE_CODES,
    key: 'diagnostic-trouble-codes',
    testId: 'sidebar-diagnostic-trouble-codes',
    isNew: newFeatureItems?.dtc,
    badgeText: newFeatureItems?.dtc ? formatMessage(newFeatureMessages.new) : undefined,
    badgeTooltipText: newFeatureItems?.dtc ? formatMessage(newFeatureMessages.newFeatureAvailable) : undefined,
  };

  const videos: IListItem = {
    label: formatMessage(messages.videos),
    to: FSEAuthRoutes.VIDEOS,
    key: 'videos',
    testId: 'sidebar-videos',
    isNew: newFeatureItems?.videos,
    badgeText: newFeatureItems?.videos ? formatMessage(newFeatureMessages.new) : undefined,
    badgeTooltipText: newFeatureItems?.videos ? formatMessage(newFeatureMessages.newFeatureAvailable) : undefined,
  };

  const ECOM = isEcomEnabled ? [ECOMSidebarItem] : [];
  const CPQ = isCpqEnabled ? [CPQSidebarItem] : [];
  const SERVICE_AND_INFO_WARRANTY = isWarrantyEnabled ? [warranty] : [];
  const SERVICE_AND_INFO_MANUALS = isManualsEnabled ? [manuals] : [];
  const SERVICE_AND_INFO_PSP = isPSPEnabled ? [psp] : [];
  const SERVICE_AND_INFO_DTC = isDTCEnabled ? [dtc] : [];
  const SERVICE_AND_INFO_VIDEOS = isVideosEnabled ? [videos] : [];

  const items: ISidebarItem[] = [
    {
      id: 'account-selector',
      label: activeAccountName,
      icon: 'business',
      trailingIcon: 'agco_chevron-up-down',
      type: 'selector',
      selectorData: {
        activeItemId: activeAccountId,
        onChange: onAccountSelect,
        subItems: accountItems,
      },
      testId: 'sidebar-account-selector',
    },
    {
      label: formatMessage(messages.dashboard),
      icon: 'home',
      to: EAuthRoutes.DASHBOARD,
      testId: 'sidebar-dashboard',
    },
    {
      label: formatMessage(messages.fleetOverview),
      icon: 'agriculture',
      id: 'fleet-overview',
      to: `${EAuthRoutes.FLEET_OVERVIEW}`,
      testId: 'sidebar-fleet-overview',
    },
    {
      label: formatMessage(messages.serviceAndInformation),
      icon: 'privacy_tip',
      type: 'list',
      id: 'Service & Information',
      testId: 'sidebar-service-and-information',
      listData: {
        basePath: isServiceAndInformationBasePathEnabled ? FSEAuthRoutes.SERVICE_AND_INFORMATION : '',
        isBasePathLinkOff: !isServiceAndInformationBasePathEnabled,
        items: [
          ...SERVICE_AND_INFO_MANUALS,
          ...SERVICE_AND_INFO_WARRANTY,
          ...SERVICE_AND_INFO_PSP,
          ...SERVICE_AND_INFO_DTC,
          ...SERVICE_AND_INFO_VIDEOS,
        ],
      },
    },
    ...ECOM,
    ...CPQ,
  ];

  return items.filter(item => !!item.to || !!item.onClick || item.type === 'selector' || item.type === 'list');
};
